/**
 * Single source of truth for Menuvera's product chrome.
 *
 * Visual language: monochrome museum wall — quiet, editorial.
 * Status/meta uses plain text labels (no pill chips). Saturated blues are
 * reserved for category accents only.
 */

export const brand = {
  name: 'Menuvera',
  /** Shown in the browser tab and the PWA manifest. */
  tagline: 'Menu books that never look templated',
  shortName: 'Menuvera',

  wordmark: {
    /** Rendered in the app bar until a real logo exists. */
    text: 'Menuvera',
    weight: 500,
    letterSpacing: '0.02em',
  },

  /** Drop an inline SVG string here to replace the wordmark everywhere. */
  logoSvg: null as string | null,

  palette: {
    /** Primary text, icons, structural lines. */
    ink: '#333333',
    /** Page canvas and card surfaces. */
    paper: '#ffffff',
    /** Hairline borders and subtle separators. */
    whisper: '#e6e6e6',
    /** Filled interactive surfaces — primary CTA, active tab. */
    carbon: '#202020',
    /** Secondary dark surfaces / muted dark text. */
    slate: '#3b3b3b',
    /** Tertiary text, secondary button borders. */
    graphite: '#555555',
    /** Body secondary / helper / placeholder. */
    ash: '#838383',
    /** Disabled controls, light strokes. */
    fog: '#bdbdbd',
    /** Muted foreground on dark surfaces. */
    mist: '#c7c7c7',
    /** Category badge fill — sole chromatic accent on chrome. */
    indigoPulse: '#4177ff',
    /** Alternating category badge fill. */
    skyPulse: '#41a9ff',
    /** Semantic statuses (not CTA chrome). */
    danger: '#9B1C1C',
    warning: '#9A6700',
    info: '#4177ff',
  },

  /** Sole UI typeface (PP Neue Montreal substitute). */
  fontFamily:
    "'Inter Variable', Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  /** Same face — product uses one typeface everywhere. */
  displayFamily:
    "'Inter Variable', Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  monoFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",

  /** Binary radius system: cards vs everything interactive. */
  radius: {
    cards: 12,
    pills: 100,
  },

  layout: {
    pageMaxWidth: 1200,
    sectionGap: 48,
    elementGap: 8,
  },

  shadow: {
    /** Floating chat / FAB only — not cards or buttons. */
    sm: 'rgba(0, 0, 0, 0.45) 0px 5px 5px 0px',
  },
} as const;

export type Brand = typeof brand;
